import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { setCurrentStreamView } from '../ducks/we-spin-duck.js';
import { useSelector, useDispatch } from 'react-redux';
import { LabelBold, Box } from '@comeon/ui';
import { selectLocale } from '~/components/account/ducks/index.js';
import useTranslations from '../../../translation/use-translations';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';
import { WE_SPIN_NEXT_SESSION_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import {
	selectSessions,
	selectUpcomingStreamFromStreamer,
	selectUpcomingStreamFromOtherStreamers
} from '../ducks/we-spin-selectors.js';
import { PAST_STATES, SESSION_ONLINE } from '../constants/we-spin-stream-states.js';
import { ROUTE_WE_SPIN_GAME_VIEW } from '../constants/we-spin-route-constants.js';
import { streamToGameRoomRoute } from '../utils/stream-to-game-room-route.js';
import WeSpinGameRoomNextSessionItem from './we-spin-game-room-next-session-item.js';

const DEFAULT_LIMIT = 3;

const findSessionForStreamRef = (sessions, streamRef) =>
	sessions.find(session => session.streamViews.some(streamView => streamView.streamRef === streamRef));

const WeSpinGameRoomNextSessionList = ({ showGameIcon = false, limit = DEFAULT_LIMIT, resources, streamerName }) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const upcomingStreamsFromStreamer = useSelector(selectUpcomingStreamFromStreamer(limit, streamerName));
	const upcomingStreamsFromOtherStreamers = useSelector(selectUpcomingStreamFromOtherStreamers(limit, streamerName));
	const sessions = useSelector(selectSessions);
	const transformPath = useContextualPathTransformer();
	const onOtherStreamClick = useCallback(
		streamView => event => {
			event.preventDefault();
			event.stopPropagation();
			const { streamRef } = streamView;
			if (streamRef) {
				const session = findSessionForStreamRef(sessions, streamRef);
				if (session) {
					const link = transformPath(
						session.state === SESSION_ONLINE || // FIXME! Needs a running state etc
							PAST_STATES.includes(session.state)
							? `${ROUTE_WE_SPIN_GAME_VIEW}/${session.sessionRef}`
							: streamToGameRoomRoute(streamView)
					);
					dispatch(setCurrentStreamView(streamView));
					navigate(link);
				}
			}
		},
		[navigate, sessions, transformPath, dispatch]
	);
	const { translations: tr, isLoadingTranslations } = useTranslations(WE_SPIN_NEXT_SESSION_TEXT_KEYS);

	const locale = useSelector(selectLocale);
	if (isLoadingTranslations) return null;
	return (
		<>
			{upcomingStreamsFromStreamer?.length > 0 && (
				<div className="u-pl1 u-pr1 u-pt2">
					<LabelBold className="u-pb1">{tr.WE_SPIN_NEXT_SESSION_LABEL}</LabelBold>
					{upcomingStreamsFromStreamer.map(sv => (
						<Box mb="5" key={sv.streamRef}>
							<WeSpinGameRoomNextSessionItem
								key={sv.streamRef}
								locale={locale}
								stream={sv}
								translations={tr}
								showGameIcon={showGameIcon}
								onClick={onOtherStreamClick(sv)}
								resources={resources}
							/>
						</Box>
					))}
				</div>
			)}
			{upcomingStreamsFromOtherStreamers?.length > 0 && (
				<div className="u-pl1 u-pr1 u-pt2">
					<LabelBold className="u-pb1">{tr.WE_SPIN_NEXT_SESSION_LABEL_OTHER_STREAMERS}</LabelBold>
					{upcomingStreamsFromOtherStreamers.map(sv => (
						<Box mb="5" key={sv.streamRef}>
							<WeSpinGameRoomNextSessionItem
								locale={locale}
								stream={sv}
								translations={tr}
								showGameIcon={showGameIcon}
								onClick={onOtherStreamClick(sv)}
								resources={resources}
							/>
						</Box>
					))}
				</div>
			)}
		</>
	);
};

export default WeSpinGameRoomNextSessionList;
